import React from "react";
import { AlertCircle, AlertTriangle, ChefHat, Clock, Flame, GlassWater, Utensils } from "lucide-react";

export default function LiveOpsStrip({ kitchenOps }) {
  if (!kitchenOps) return null;

  const { summary = {}, stations = {} } = kitchenOps;

  const getStationIcon = (key) => {
    switch (key) {
      case "bar":
        return <GlassWater className="h-4 w-4 text-primary" />;
      case "tandoor":
        return <Flame className="h-4 w-4 text-amber-500" />;
      default:
        return <ChefHat className="h-4 w-4 text-foreground" />;
    }
  };

  const stationList = [
    { key: "main_kitchen", label: "Main Kitchen", data: stations.main_kitchen || {} },
    { key: "tandoor", label: "Tandoor Station", data: stations.tandoor || {} },
    { key: "bar", label: "Bar & Beverage", data: stations.bar || {} },
  ];

  return (
    <section className="rounded-lg border bg-card p-loose space-y-base" data-testid="dash-live-kitchen-ops">
      <div className="flex flex-wrap items-center justify-between gap-snug border-b pb-snug">
        <div className="flex items-center gap-snug">
          <Utensils className="h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="text-lede font-semibold">Live Kitchen & Station Operations</h2>
            <p className="text-meta text-muted-foreground">
              Real-time multi-station KOT routing and delay monitoring
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-base text-meta">
          <div className="flex items-center gap-tight">
            <span className="text-muted-foreground">Active Tickets:</span>
            <span className="font-bold tabular-nums text-foreground">{summary.active_tickets_count || 0}</span>
          </div>
          <div className="flex items-center gap-tight">
            <span className="text-muted-foreground">Waiting:</span>
            <span className="font-semibold tabular-nums text-primary">{summary.waiting_items || 0}</span>
          </div>
          <div className="flex items-center gap-tight">
            <span className="text-muted-foreground">Cooking:</span>
            <span className="font-semibold tabular-nums text-amber-600 dark:text-amber-400">{summary.cooking_items || 0}</span>
          </div>
          {summary.overdue_tickets_count > 0 && (
            <span className="inline-flex items-center gap-tight rounded bg-danger-subtle px-snug py-hair font-semibold text-danger">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>{summary.overdue_tickets_count} Overdue</span>
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-base md:grid-cols-3">
        {stationList.map(({ key, label, data }) => {
          const isCrit = data.overdue_crit > 0;
          const isWarn = data.overdue_warn > 0;
          const borderStyle = isCrit
            ? "border-danger/40 bg-danger-subtle/20"
            : isWarn
            ? "border-amber-500/40 bg-amber-500/10"
            : "border-border";

          return (
            <div key={key} className={`rounded-md border p-base space-y-snug ${borderStyle}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-tight font-semibold text-body">
                  {getStationIcon(key)}
                  <span>{label}</span>
                </div>
                {data.oldest_ticket_min > 0 && (
                  <span
                    className={`inline-flex items-center gap-hair text-meta font-medium tabular-nums ${
                      data.oldest_ticket_min >= 20
                        ? "text-danger"
                        : data.oldest_ticket_min >= 15
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground"
                    }`}
                  >
                    <Clock className="h-3 w-3" />
                    <span>Oldest: {data.oldest_ticket_min}m</span>
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-snug pt-tight border-t">
                <div className="rounded bg-background/60 p-tight text-center">
                  <div className="text-[11px] text-muted-foreground">Waiting</div>
                  <div className="text-body font-bold tabular-nums text-primary">{data.waiting || 0}</div>
                </div>
                <div className="rounded bg-background/60 p-tight text-center">
                  <div className="text-[11px] text-muted-foreground">Cooking</div>
                  <div className="text-body font-bold tabular-nums text-amber-600 dark:text-amber-400">
                    {data.cooking || 0}
                  </div>
                </div>
              </div>

              {(data.overdue_warn > 0 || data.overdue_crit > 0) && (
                <div className="flex items-center justify-between text-[11px] font-medium pt-hair">
                  {data.overdue_warn > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {data.overdue_warn} ticket(s) &gt; 15m
                    </span>
                  )}
                  {data.overdue_crit > 0 && (
                    <span className="text-danger font-bold">
                      {data.overdue_crit} ticket(s) &gt; 25m!
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
